{
  "blocks": [
    {
      "id": "test-pgt-a",
      "kind": "test",
      "tags": ["testing", "genetic-screening", "during-cycle"],
      "title": "PGT-A (Embryo Screening)",
      "subtitle": "Screens embryos for chromosomal abnormalities before transfer",
      "icon": "🧬",
      "body": {
        "testName": "Preimplantation Genetic Testing for Aneuploidy (PGT-A)",
        "category": "genetic",
        "parentTest": {
          "testId": "test-preimplantation-genetic-testing",
          "badgeLabel": "View all PGT tests"
        },
        "evidenceStrength": "mixed",
        "whatItMeasures": "Preimplantation genetic testing for aneuploidy screens embryo biopsy cells for missing or extra chromosomes before deciding which embryo to transfer.\n\nThere are 23 chromosome pairs in each embryo. PGT-A classifies embryos as euploid, aneuploid, mosaic, or sometimes no-result.\n\nIt helps prioritize embryos to transfer, but it is not proven to improve cumulative live birth for every IVF patient.",
        "howItWorks": "An embryologist biopsies 5-10 trophectoderm cells from a day 5-7 blastocyst. Embryos are usually vitrified while cells are sent to a genetics lab. The lab uses chromosome copy-number testing, often next-generation sequencing, and the clinic uses the result to decide which embryo to transfer first or which embryos not to transfer. If you are using a broader embryo sequencing platform, ask whether PGT-A-style aneuploidy screening is already included in that report.",
        "quickFacts": [
          {
            "kind": "sample",
            "value": "Embryo biopsy cells"
          },
          {
            "kind": "results",
            "value": "7-10 business days"
          }
        ],
        "soldAs": [
          {
            "name": "Orchid",
            "company": "Orchid",
            "description": "A whole-genome embryo screening profile that includes PGT-A-style chromosome findings and may add broader genomic information from the same biopsy sample.",
            "evidenceSummary": "The commercial profile should be checked for the exact genomic layers, workflow, and availability included in the current offering.",
            "linkedTestId": "test-pgt-a-products-orchid"
          },
          {
            "name": "Herasight",
            "company": "Herasight",
            "description": "A broader embryo analysis profile that can build on PGT-A data and parental sequencing.",
            "evidenceSummary": "The commercial profile should be checked for current data requirements, report scope, and compatibility with the clinic's PGT-A workflow.",
            "linkedTestId": "test-pgt-a-products-herasight"
          }
        ],
        "resultTypes": [
          {
            "label": "Euploid",
            "meaning": "The embryo biopsy sample shows the expected number of chromosomes.",
            "whatToExpect": "Clinics usually prioritize euploid embryos for transfer, but a euploid result still does not guarantee implantation, pregnancy, or live birth."
          },
          {
            "label": "Aneuploid",
            "meaning": "The embryo biopsy sample shows missing or extra chromosomes.",
            "whatToExpect": "Most clinics do not recommend transfer because implantation is less likely and miscarriage risk is higher, but policies can vary for unusual or segmental findings."
          },
          {
            "label": "Mosaic",
            "meaning": "The biopsy sample shows a mix of cells, with some appearing chromosomally normal and some abnormal.",
            "whatToExpect": "This result needs careful genetic counseling because transfer decisions depend on the chromosome involved, mosaic level, embryo quality, clinic policy, and whether euploid embryos are available."
          },
          {
            "label": "No-result",
            "meaning": "The lab could not produce a reliable chromosome result from the biopsy sample.",
            "whatToExpect": "Your clinic may discuss re-biopsy, transferring without a result, or deprioritizing the embryo depending on embryo quality and how many embryos are available."
          }
        ],
        "expectations": {
          "canExpect": [
            "A chromosome-status report for each embryo that can be biopsied",
            "Some broader embryo sequencing reports include PGT-A plus other genomic information from the same biopsy",
            "Can help prioritize embryos for transfer when there are multiple blastocysts to compare",
            "May reduce transfers of embryos unlikely to implant because of chromosome abnormalities",
            "May be more useful in older patients with good embryo yield than in young donor-egg or low-yield cycles"
          ],
          "shouldNotExpect": [
            "ASRM says its value as a routine screening test for all IVF patients has not been demonstrated; ESHRE does not recommend routine add-on use",
            "It may reduce miscarriage in some contexts, but that has not consistently translated into higher cumulative live birth",
            "Testing may leave no embryo to transfer, including when there are few blastocysts or mosaic or uncertain results",
            "It does not guarantee implantation, pregnancy, or live birth, and it does not test for single-gene conditions unless paired with PGT-M"
          ]
        },
        "considerations": [
          "Ask exactly what the result would change: transfer order, miscarriage-risk reduction, avoiding aneuploid transfer, or whether to do another retrieval",
          "Ask whether PGT-A is already included if you are using a whole-genome embryo screening or PGT-P platform",
          "Ask whether the recommendation is based on age, embryo number, recurrent loss, prior aneuploid pregnancy, or simply clinic routine",
          "Ask before testing how your clinic handles mosaic, segmental, and no-result embryos, including whether genetic counseling is available",
          "Genetic counseling is useful before and after testing, especially when results are complex"
        ],
        "bestFor": [
          "Multiple blastocysts to choose from",
          "Advanced maternal age with good embryo yield",
          "Recurrent pregnancy loss with suspected aneuploidy",
          "Not proven as routine screening for every IVF cycle"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Some whole-genome embryo screening platforms include PGT-A-style chromosome screening from the same biopsy",
            "mechanism": "Broader sequencing can return aneuploidy information plus additional genomic layers, so patients should verify what is already included before buying separate tests.",
            "source": "Orchid PGT-WGS product information, 2026"
          },
          {
            "direction": "neutral",
            "outcome": "ASRM states that the value of PGT-A as routine screening for all IVF patients has not been demonstrated",
            "mechanism": "Recent randomized trials found similar overall pregnancy outcomes between PGT-A and conventional IVF in many patients with available blastocysts",
            "source": "ASRM Practice Committee Opinion, Fertility and Sterility 2024"
          },
          {
            "direction": "neutral",
            "outcome": "The STAR multicenter trial found similar ongoing pregnancy outcomes with PGT-A and morphology-based selection",
            "magnitude": "Intention-to-treat ongoing pregnancy: 41.8% PGT-A vs 43.5% control",
            "source": "STAR trial summarized in ASRM 2024 committee opinion"
          },
          {
            "direction": "positive",
            "outcome": "PGT-A may have a role in selected older patients, especially when embryo yield is high",
            "mechanism": "Aneuploidy rises with age, so identifying euploid embryos may reduce futile transfers in selected cases",
            "source": "ASRM Practice Committee Opinion, 2024"
          },
          {
            "direction": "negative",
            "outcome": "A large randomized trial found conventional IVF was noninferior to PGT-A for cumulative live birth in younger good-prognosis patients",
            "magnitude": "Cumulative live birth: 77.2% PGT-A vs 81.8% conventional IVF",
            "source": "2021 multicenter RCT summarized in ASRM 2024 committee opinion"
          },
          {
            "direction": "neutral",
            "outcome": "ASRM's recurrent pregnancy loss guidance says PGT-A has not clearly improved live birth versus expectant management in RPL, but discussion may be reasonable for some patients over 40 with a proven aneuploid miscarriage",
            "source": "ASRM recurrent pregnancy loss committee opinion, 2026"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend PGT-A for routine clinical use as an add-on",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023"
          }
        ],
        "headlineStat": "Not routine",
        "turnaroundTime": "7-10 business days",
        "costRange": {
          "low": 3000,
          "high": 6000
        },
        "atHome": false,
        "clinicRequired": false,
        "dataPoints": [],
        "section": "genetic-screening"
      },
      "sources": [
        {
          "title": "Preimplantation genetic testing for aneuploidies: where we are and where we're going (STAR trial)",
          "authors": "Munné S et al.",
          "journal": "Fertility and Sterility",
          "year": 2019,
          "url": "https://pubmed.ncbi.nlm.nih.gov/31551155/",
          "evidenceLevel": "rct"
        },
        {
          "title": "Live Birth with or without Preimplantation Genetic Testing for Aneuploidy",
          "authors": "Yan J et al.",
          "journal": "New England Journal of Medicine",
          "year": 2021,
          "url": "https://pubmed.ncbi.nlm.nih.gov/34818479/",
          "evidenceLevel": "rct"
        },
        {
          "title": "The use of preimplantation genetic testing for aneuploidy: a committee opinion",
          "authors": "Practice Committees of the American Society for Reproductive Medicine and the Society for Assisted Reproductive Technology",
          "journal": "Fertility and Sterility",
          "year": 2024,
          "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/the-use-of-preimplantation-genetic-testing-for-aneuploidy-a-committee-opinion-2024/",
          "evidenceLevel": "guideline"
        },
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "authors": "ESHRE Add-ons working group; Lundin K, Bentzen JG, et al.",
          "journal": "Human Reproduction",
          "year": 2023,
          "url": "https://pubmed.ncbi.nlm.nih.gov/37747409/",
          "evidenceLevel": "guideline"
        },
        {
          "title": "Recurrent pregnancy loss: a committee opinion",
          "authors": "Practice Committee of the American Society for Reproductive Medicine",
          "journal": "ASRM Practice Committee",
          "year": 2026,
          "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/",
          "evidenceLevel": "guideline"
        }
      ],
      "readTimeMinutes": 5
    }
  ],
  "relationships": [],
  "localeVariants": []
}
